import { useEffect } from 'react'
import type { GetServerSidePropsContext } from 'next'
import type { Product } from '@mc/api'
import { trackBogoExperimentViewed } from '@mc/bogo'
import { StaticDataProvider } from '@mc/static-data-react'
import { EventTypes } from '@mc/track-event'
import { SimplifiedFooter } from 'components/Footer/SimplifiedFooter'
import { trackPen7367BackendEV } from 'experiments/pen-7367_v1_quiz_subcat_removal/server'
import { trackPrcng1138BackendEV } from 'experiments/prcng-1138_v1_certs_quiz_plans/server'
import { useGrowthQuizCategoryMedia } from 'libs/client/api/growth'
import {
  getDefaultProduct,
  getGrowthCategoriesAndSubcategories,
  getFreeTrialByToken,
} from 'libs/server/api'
import { getPageData } from 'libs/server/page-data'
import { pageErrorHandler } from 'libs/server/page-error-handler'
import { useSprig } from 'libs/sprig'
import { PageComponent } from 'types/components/pages'
import Meta from './components/Meta'
import { Quiz } from './components/Quiz'
import { TCategory, TSubcategory } from './types'

interface FindMyClassesProps {
  annualPass?: Product | null
  categories: TCategory[]
  subcategories: TSubcategory[]
}

const FindMyClasses: PageComponent<FindMyClassesProps> = ({
  annualPass,
  categories,
  subcategories,
}: FindMyClassesProps) => {
  useEffect(() => {
    trackBogoExperimentViewed()
  })

  useSprig()

  // preload category media data used to display full access step video
  useGrowthQuizCategoryMedia()

  return (
    <StaticDataProvider
      value={{
        annualPass,
        categories,
        subcategories,
      }}
    >
      <Meta />
      <Quiz />
    </StaticDataProvider>
  )
}

FindMyClasses.pageSettings = {
  analyticsLabel: EventTypes.CLASS_RECOMMENDATIONS_PAGE_VIEWED,
  navigation: null,
  footer: SimplifiedFooter,
}

export const getServerSideProps = async (ctx: GetServerSidePropsContext) => {
  const {
    req: {
      cookies: { referral_token: referralToken },
    },
  } = ctx

  let categories: TCategory[] = []
  let subcategories: TSubcategory[] = []

  try {
    const data = await getGrowthCategoriesAndSubcategories(ctx)
    categories = data.categories.map((cat) => ({
      id: cat.id,
      name: cat.name,
      slug: cat.slug,
    }))
    subcategories = data.subcategories.map((cat) => ({
      id: cat.id,
      name: cat.name,
      slug: cat.slug,
      parentId: cat.parentId as number,
    }))
  } catch (error) {
    return pageErrorHandler(error)
  }

  await trackPrcng1138BackendEV(ctx)
  await trackPen7367BackendEV(ctx)

  return getPageData({
    nonCriticalPromises: {
      annualPass: getDefaultProduct(ctx),
      trial: getFreeTrialByToken(ctx, { referralToken }),
    },
    propsMapper: (props) => ({
      ...props,
      categories,
      subcategories,
    }),
  })
}

export default FindMyClasses
